import { Editor, Location, NodeEntry, Path, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';
import { nanoid } from 'nanoid';
import { Element } from 'slate/dist/interfaces/element';

import { getNodes } from '../../../slatePlugins/common/queries/getNodes';

export const getNodeAtEdge = (editor: Editor, type: string) => {
  const { selection } = editor;
  if (selection) {
    const { anchor } = selection;
    const startPoint = Editor.before(editor, anchor, { unit: 'offset', distance: 1 });
    const endPoint = Editor.after(editor, anchor, { unit: 'offset', distance: 1 });
    const [firstItem] = Editor.nodes(editor, {
      at: { anchor: startPoint, focus: endPoint },
      match: (n: any) => n.type === type,
    });
    return firstItem;
  }
  return null;
};

export const insertNewLine = (editor: Editor, defaultType = 'p', options = {}) => {
  const newLine = {
    id: nanoid(16),
    type: defaultType,
    children: [
      {
        text: '',
        marks: [],
      },
    ],
  };
  Transforms.insertNodes(editor, newLine, { ...options });
};

export const insertBlankText = ([child, childPath], editor, options = {}) => {
  Transforms.insertNodes(editor, { text: ' ' } as any, { at: childPath, ...options });
};

export const isLastChild = (editor, target: Location): boolean =>
  !Editor.next(editor, { at: target });

export const getElement = (editor, type) => {
  const [element]: Element[] = Array.from(
    getNodes(editor, {
      match: { type } as any,
    }),
    ([node]) => node as Element
  );
  return element;
};

export const getElementId = (editor, type) => {
  return getElement(editor, type)?.id;
};

export const findAboveByType = (
  editor: any,
  current: Element | Path,
  parentNodeType?: string,
  options?: {
    match?: (node: any) => boolean;
    mode?: 'highest' | 'lowest';
    voids?: boolean;
  }
): ReturnType<typeof Editor.above> => {
  const path = Path.isPath(current) ? current : ReactEditor.findPath(editor, current);
  const { match = () => true, ...opts } = options ?? {};
  try {
    return Editor.above(editor, {
      at: path,
      match: (n: any) => (parentNodeType ? n.type === parentNodeType : true) && match(n),
      ...opts,
    });
    // eslint-disable-next-line no-empty
  } catch (ex) {}
  return undefined;
};

export const findBellowByType = (
  editor: any,
  current: Element | Path,
  childType?: string,
  options?: {
    mode?: 'all' | 'highest' | 'lowest';
    universal?: boolean;
    reverse?: boolean;
    voids?: boolean;
    match?: (node: any) => boolean;
  }
): NodeEntry<any>[] => {
  const { match = () => true, ...opts } = options ?? {};
  const path = Path.isPath(current) ? current : ReactEditor.findPath(editor, current);
  return Array.from(
    Editor.nodes(editor, {
      at: path,
      match: (n: any) => (childType ? n.type === childType : true) && match(n),
      ...opts,
    })
  );
};
